import { get, post } from '../utils/networking';
import type { HmsNotification, SafetyRepTask } from './safetyRepWork';
import type { DeviationSummary, MyTasks } from './safetyRounds';
import type { FollowUpItem } from './hrWorkEnvironment';
import type { MeasureDeadline } from './measureDeadlines';
import type { HrHome } from './hrHome';

export type NotificationSource =
  | 'safetyRep'
  | 'safetyRounds'
  | 'hr'
  | 'teamLeader';

export type TeamLeaderTask = {
  kind: 'plan' | 'review' | 'askSignature' | 'registerNoSignature' | 'signatureReturned';
  /** The deadline of a plan or a review. The signature has none. */
  date: string | null;
  teamId: number;
  team: string;
  surveyId: number;
  title: string;
};

/** What waits for the user in each module they belong to. A part is null without the role or the module. */
/** A task with the keys of its areas, which are the same in every language. Missing from older backends. */
export type CenterSafetyRepTask = SafetyRepTask & { areaKeys?: string[] };

/** Only the measures past their deadline. The area is named only when there is one, as several are shown as one. */
export type CenterMeasure = Pick<MeasureDeadline, 'id' | 'title' | 'team' | 'teamId' | 'surveyId' | 'dueDate' | 'daysLeft'> & {
  area: string | null;
};

/** A protocol the safety representative signed that still waits for the general manager */
export type CenterProtocolWaiting = { roundId: number; planName: string; signedOn: string; days: number };

export type NotificationCenter = {
  safetyRep: {
    tasks: CenterSafetyRepTask[];
    /** Those not read, and the latest read */
    notifications: HmsNotification[];
    /** The teams the user acts for, whose surveys they can open. Missing from older backends. */
    teamIds?: number[];
  } | null;
  safetyRounds: { tasks: MyTasks; deviations: DeviationSummary[] } | null;
  hr: {
    followUp: FollowUpItem[];
    measures: CenterMeasure[];
    pulse: HrHome['pulse']['open'];
    /** Protocols that have waited for the general manager for 14 days or more. Missing from older backends. */
    protocolsWaiting?: CenterProtocolWaiting[];
  } | null;
  teamLeader: TeamLeaderTask[] | null;
  /** The parts that could not be fetched this time */
  unavailable: NotificationSource[];
  /** What was in the list when the user last opened the bell */
  seen: string[];
};

export const getNotificationCenter = (language: string) =>
  get<NotificationCenter>('/api/me/notifications', { params: { language } });

/** The user opened the bell: what is in the list now is no longer new */
export const markNotificationsSeen = (keys: string[]) =>
  post<void>('/api/me/notifications/seen', { keys });
